import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { apiClient } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Users,
  UserPlus,
  Mail,
  Shield,
  Search,
  UserCheck,
  UserX,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  Building2,
  Copy,
  Link as LinkIcon,
} from 'lucide-react';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showEditRolesDialog, setShowEditRolesDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [updatingRoles, setUpdatingRoles] = useState(false);
  const [activeTab, setActiveTab] = useState('users');

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const permissions = currentUser.permissions || [];
  const canManageUsers = permissions.includes('manage_users');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, rolesRes, invitationsRes] = await Promise.all([
        apiClient.get('/users'),
        apiClient.get('/roles'),
        canManageUsers ? apiClient.get('/invitations') : Promise.resolve({ data: [] }),
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
      setInvitations(invitationsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async () => {
    if (!inviteEmail || !selectedRoleIds.length) {
      toast.error('Please enter email and select at least one role');
      return;
    }

    setInviting(true);
    try {
      const response = await apiClient.post('/invitations', {
        email: inviteEmail,
        role_ids: selectedRoleIds,
      });
      
      // Show success with invite link option
      const inviteLink = response.data.invite_link;
      if (inviteLink) {
        toast.success(
          <div>
            <p className="font-medium">Invitation sent!</p>
            <p className="text-sm mt-1">Link: <a href={inviteLink} target="_blank" rel="noopener noreferrer" className="underline">{inviteLink.substring(0, 50)}...</a></p>
            <button 
              onClick={() => {
                navigator.clipboard.writeText(inviteLink);
                toast.success('Link copied!');
              }}
              className="text-sm underline mt-1"
            >
              Copy Link
            </button>
          </div>,
          { duration: 10000 }
        );
      } else {
        toast.success('Invitation sent successfully');
      }
      
      setShowInviteDialog(false);
      setInviteEmail('');
      setSelectedRoleIds([]);
      fetchData();
    } catch (error) {
      console.error('Error inviting user:', error);
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleUpdateRoles = async () => {
    if (!selectedUser || !selectedRoleIds.length) return;

    setUpdatingRoles(true);
    try {
      await apiClient.patch(`/users/${selectedUser.id}/roles`, selectedRoleIds);
      toast.success('User roles updated');
      setShowEditRolesDialog(false);
      setSelectedUser(null);
      setSelectedRoleIds([]);
      fetchData();
    } catch (error) {
      console.error('Error updating roles:', error);
      toast.error(error.response?.data?.detail || 'Failed to update roles');
    } finally {
      setUpdatingRoles(false);
    }
  };

  const handleToggleUserStatus = async (user) => {
    try {
      await apiClient.patch(`/users/${user.id}/status`, null, {
        params: { is_active: !user.is_active },
      });
      toast.success(`User ${user.is_active ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      await apiClient.delete(`/invitations/${invitationId}`);
      toast.success('Invitation cancelled');
      fetchData();
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      toast.error('Failed to cancel invitation');
    }
  };

  const openEditRolesDialog = (user) => {
    setSelectedUser(user);
    setSelectedRoleIds(user.role_ids || []);
    setShowEditRolesDialog(true);
  };

  const toggleRole = (roleId) => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    );
  };

  const filteredUsers = users.filter(
    (user) =>
      user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const pendingInvitations = invitations.filter((inv) => inv.status === 'pending');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading-spinner">
        <RefreshCw className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="user-management-page">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users size={28} />
            Team Management
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage your organization's team members and invitations
          </p>
        </div>
        {canManageUsers && (
          <Button onClick={() => setShowInviteDialog(true)} data-testid="invite-user-btn">
            <UserPlus size={18} className="mr-2" />
            Invite Team Member
          </Button>
        )}
      </div>

      {/* Organization Info Card */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-6 text-white mb-6">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white/10 rounded-lg">
            <Building2 size={32} />
          </div>
          <div>
            <h2 className="text-xl font-semibold">{currentUser.organization_name || 'Your Organization'}</h2>
            <p className="text-slate-300 text-sm">
              {users.length} team member{users.length !== 1 ? 's' : ''} &bull;{' '}
              {pendingInvitations.length} pending invitation{pendingInvitations.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'users'
              ? 'border-slate-900 text-slate-900'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          data-testid="users-tab"
        >
          <Users size={16} className="inline mr-2" />
          Team Members ({users.length})
        </button>
        {canManageUsers && (
          <button
            onClick={() => setActiveTab('invitations')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'invitations'
                ? 'border-slate-900 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid="invitations-tab"
          >
            <Mail size={16} className="inline mr-2" />
            Invitations ({pendingInvitations.length})
          </button>
        )}
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div>
          {/* Search */}
          <div className="mb-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="user-search-input"
              />
            </div>
          </div>

          {/* Users Table */}
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Roles</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Joined</th>
                  {canManageUsers && (
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-slate-50" data-testid={`user-row-${user.id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {user.picture ? (
                          <img
                            src={user.picture}
                            alt={user.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                            <span className="text-lg font-medium text-slate-600">
                              {user.name?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-slate-900">
                            {user.name}
                            {user.is_org_owner && (
                              <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                                Owner
                              </span>
                            )}
                            {user.id === currentUser.id && (
                              <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                                You
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-slate-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {(user.role_names || []).map((role, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium bg-slate-100 text-slate-700 rounded"
                          >
                            <Shield size={12} className="mr-1" />
                            {role}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {user.is_active ? (
                        <span className="inline-flex items-center text-green-700 text-sm">
                          <UserCheck size={16} className="mr-1" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-red-600 text-sm">
                          <UserX size={16} className="mr-1" />
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-500">
                      {user.created_at
                        ? new Date(user.created_at).toLocaleDateString()
                        : '-'}
                    </td>
                    {canManageUsers && (
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditRolesDialog(user)}
                            disabled={user.id === currentUser.id}
                            data-testid={`edit-roles-btn-${user.id}`}
                          >
                            Edit Roles
                          </Button>
                          {!user.is_org_owner && user.id !== currentUser.id && (
                            <Button
                              variant={user.is_active ? 'destructive' : 'default'}
                              size="sm"
                              onClick={() => handleToggleUserStatus(user)}
                              data-testid={`toggle-status-btn-${user.id}`}
                            >
                              {user.is_active ? 'Deactivate' : 'Activate'}
                            </Button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan={canManageUsers ? 5 : 4} className="px-6 py-12 text-center text-slate-500">
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Invitations Tab */}
      {activeTab === 'invitations' && canManageUsers && (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Roles</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Invited</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {invitations.map((inv) => (
                <tr key={inv.id} className="hover:bg-slate-50" data-testid={`invitation-row-${inv.id}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Mail size={18} className="text-slate-400" />
                      <span className="text-slate-900">{inv.email}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {(inv.role_names || []).map((role, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium bg-slate-100 text-slate-700 rounded"
                        >
                          {role}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {inv.status === 'pending' && (
                      <span className="inline-flex items-center text-amber-600 text-sm">
                        <Clock size={16} className="mr-1" />
                        Pending
                      </span>
                    )}
                    {inv.status === 'accepted' && (
                      <span className="inline-flex items-center text-green-600 text-sm">
                        <CheckCircle size={16} className="mr-1" />
                        Accepted
                      </span>
                    )}
                    {(inv.status === 'expired' || inv.status === 'cancelled') && (
                      <span className="inline-flex items-center text-red-600 text-sm">
                        <XCircle size={16} className="mr-1" />
                        {inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-500">
                    {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {inv.status === 'pending' && (
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const frontendUrl = window.location.origin;
                            const inviteLink = `${frontendUrl}/accept-invite?token=${inv.token}`;
                            navigator.clipboard.writeText(inviteLink);
                            toast.success('Invite link copied to clipboard!');
                          }}
                          data-testid={`copy-invite-link-${inv.id}`}
                        >
                          <Copy size={14} className="mr-1" />
                          Copy Link
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCancelInvitation(inv.id)}
                          data-testid={`cancel-invitation-btn-${inv.id}`}
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {invitations.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                    No invitations found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Invite User Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus size={20} />
              Invite Team Member
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Email Address *</Label>
              <Input
                type="email"
                placeholder="colleague@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                data-testid="invite-email-input"
              />
            </div>
            <div>
              <Label>Assign Roles *</Label>
              <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
                {roles.map((role) => (
                  <label
                    key={role.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedRoleIds.includes(role.id)
                        ? 'border-slate-900 bg-slate-50'
                        : 'border-slate-200 hover:bg-slate-50'
                    }`}
                    data-testid={`role-checkbox-${role.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedRoleIds.includes(role.id)}
                      onChange={() => toggleRole(role.id)}
                      className="rounded"
                    />
                    <div>
                      <div className="font-medium text-slate-900">{role.name}</div>
                      {role.description && (
                        <div className="text-xs text-slate-500">{role.description}</div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-4">
              <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleInviteUser} disabled={inviting} data-testid="send-invite-btn">
                {inviting ? 'Sending...' : 'Send Invitation'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Roles Dialog */}
      <Dialog open={showEditRolesDialog} onOpenChange={setShowEditRolesDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield size={20} />
              Edit Roles for {selectedUser?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {roles.map((role) => (
                <label
                  key={role.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedRoleIds.includes(role.id)
                      ? 'border-slate-900 bg-slate-50'
                      : 'border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedRoleIds.includes(role.id)}
                    onChange={() => toggleRole(role.id)}
                    className="rounded"
                  />
                  <div>
                    <div className="font-medium text-slate-900">{role.name}</div>
                    {role.description && (
                      <div className="text-xs text-slate-500">{role.description}</div>
                    )}
                  </div>
                </label>
              ))}
            </div>
            <div className="flex gap-2 justify-end pt-4">
              <Button variant="outline" onClick={() => setShowEditRolesDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleUpdateRoles}
                disabled={updatingRoles || selectedRoleIds.length === 0}
                data-testid="save-roles-btn"
              >
                {updatingRoles ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
